import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { Link, useLocation } from "react-router-dom";
import { Plus, Eye } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatCurrencyCOP, formatDate } from "../../utils/format";
import LoadingOverlay from "../../components/LoadingOverlay";


function Entries() {
  const [entries, setEntries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    supplierId: "all",
  });

  const location = useLocation();

  useEffect(() => {
    fetchSuppliers();
    fetchEntries();

    if (location.state?.successMessage) {
      setTimeout(() => {
        toast.success(location.state.successMessage);
      }, 300);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchSuppliers = () => {
    api
      .get("/suppliers")
      .then((res) => setSuppliers(res.data))
      .catch(() => toast.error("Error al cargar proveedores"));
  };
  const fetchEntries = async () => {
    try {
      setLoading(true);
      const res = await api.get("/entries", {
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          supplierId: filters.supplierId,
        },
      });
      setEntries(res.data || []);
    } catch (error) {
      toast.error("Error al cargar entradas");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchEntries();
  };

  const handleClear = () => {
    setFilters({
      startDate: "",
      endDate: "",
      supplierId: "all",
    });
    setEntries([]);
    fetchEntries();
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md relative">
      <LoadingOverlay loading={loading} />

      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-semibold text-gray-800">Entradas</h2>
        <Link
          to="/entries/create"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 transition duration-300"
        >
          <Plus size={18} />
          Nueva Entrada
        </Link>
      </div>

      {/* 🔵 FILTROS */}
      <div className="bg-gray-50 p-4 rounded-lg shadow mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-semibold">Fecha Inicial</label>
          <input
            type="date"
            className="border p-2 rounded w-full"
            value={filters.startDate}
            onChange={(e) =>
              setFilters({ ...filters, startDate: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block text-sm font-semibold">Fecha Final</label>
          <input
            type="date"
            className="border p-2 rounded w-full"
            value={filters.endDate}
            onChange={(e) =>
              setFilters({ ...filters, endDate: e.target.value })
            }
          />
        </div>

        <div>
          <label className="block text-sm font-semibold">Proveedor</label>
          <select
            className="border p-2 rounded w-full"
            value={filters.supplierId}
            onChange={(e) =>
              setFilters({ ...filters, supplierId: e.target.value })
            }
          >
            <option value="all">Todos</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end gap-3">
          <button
            onClick={handleFilter}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg w-full"
          >
            Filtrar
          </button>

          <button
            onClick={handleClear}
            className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg w-full"
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* 🔵 TABLA */}
      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase tracking-wider">
              <th className="px-6 py-3">Proveedor</th>
              <th className="px-6 py-3">Fecha</th>
              <th className="px-6 py-3">Total Compra</th>
              <th className="px-6 py-3 text-center w-28">Acciones</th>
            </tr>
          </thead>
          <tbody className="text-center">
            {entries.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-6 text-center text-gray-400 italic">
                  No hay registros.
                </td>
              </tr>
            ) : (
              entries.map((entry, index) => (
                <tr
                  key={entry.id}
                  className={`border-t ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                >
                  <td className="px-6 py-4">{entry.supplier?.name || "Sin proveedor"}</td>
                  <td className="px-6 py-4">{formatDate(entry.date)}</td>
                  <td className="px-6 py-4">{formatCurrencyCOP(entry.total_purchase)}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      <Link
                        to={`/entries/${entry.id}`}
                        className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg shadow transition-all duration-300"
                        title="Ver Detalles"
                      >
                        <Eye size={16} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Entries;
